import type { Schemas } from '@crm/contracts';
import { unwrap, type Api } from '../api/client.ts';
import { dict } from '../lib/own.ts';

export type ImportJob = Schemas['ImportJob'];
export type ImportRow = Schemas['ImportRow'];
export type ImportOutcome = Schemas['ImportRowOutcome'];

export const MAX_IMPORT_BYTES = 5 * 1024 * 1024;
export const ACCEPTED_EXTENSIONS = ['.xlsx', '.csv'];

/** Champs cibles d'une colonne (contrat `ImportField`, sans les champs personnalisés `extra.<code>`). */
export const IMPORT_FIELDS = [
  'external_id', 'code', 'name', 'trade_name', 'account_type', 'segment', 'entity', 'sector', 'owner_email', 'kind', 'status',
  'phone', 'phone2', 'email', 'tax_id', 'registration_no', 'address', 'city', 'latitude', 'longitude', 'credit_limit', 'currency',
  'notes', 'contact_name', 'contact_job_title', 'contact_phone', 'contact_email',
] as const;

/** Genres d'import (OpenAPI 2.1.1 : créances de l'ERP `invoices`, `receipts`, `balances`, contrat encaissement § 6). */
export type ImportKind = Schemas['ImportKind'];
export const IMPORT_KINDS: ImportKind[] = ['accounts', 'products', 'prices', 'invoices', 'receipts', 'balances', 'targets'];

/** Champs des imports d'articles (pivot Article) et de prix (contrat `ImportField`, par genre). */
export const PRODUCT_IMPORT_FIELDS = ['external_id', 'code', 'name', 'entity', 'family', 'unit', 'tax_code', 'status', 'description', 'barcode', 'min_order_qty'] as const;
export const PRICE_IMPORT_FIELDS = ['price_list', 'product', 'min_quantity', 'unit_price'] as const;

/**
 * Champs des imports de créances (OpenAPI 2.1.1, `ImportField`) : clients par code ERP puis code CRM (`account`),
 * filiale (`entity`), montants en chaînes décimales ; `status` d'une facture : seulement « Annulée » (sinon déduit).
 */
export const INVOICE_IMPORT_FIELDS = ['external_id', 'number', 'kind', 'account', 'entity', 'invoice_date', 'due_date', 'currency', 'total_ht', 'total_tax', 'total_ttc', 'amount_paid', 'balance', 'status', 'order_ref'] as const;
export const RECEIPT_IMPORT_FIELDS = ['external_id', 'number', 'account', 'entity', 'date', 'amount', 'currency', 'method', 'reference', 'invoices', 'receipt_number'] as const;
export const BALANCE_IMPORT_FIELDS = ['account', 'entity', 'as_of', 'balance', 'amount_basis', 'overdue_amount', 'oldest_overdue_days', 'currency'] as const;

/**
 * Champs de l'import d'objectifs (OpenAPI 2.2.1, `ImportKind`) : mois, filiale, secteur (code), commercial par e-mail,
 * indicateur, famille, article, devise, unité, valeur. Niveau déduit par le serveur.
 */
export const TARGET_IMPORT_FIELDS = ['period', 'entity', 'sector', 'owner_email', 'metric', 'family', 'product', 'currency', 'unit', 'value'] as const;

export function fieldsOf(kind: ImportKind): readonly string[] {
  switch (kind) {
    case 'products':
      return PRODUCT_IMPORT_FIELDS;
    case 'prices':
      return PRICE_IMPORT_FIELDS;
    case 'invoices':
      return INVOICE_IMPORT_FIELDS;
    case 'receipts':
      return RECEIPT_IMPORT_FIELDS;
    case 'balances':
      return BALANCE_IMPORT_FIELDS;
    case 'targets':
      return TARGET_IMPORT_FIELDS;
    default:
      return IMPORT_FIELDS;
  }
}

/**
 * Droit de gérer ce que l'import écrit, exigé par l'API en plus d'`imports.run` pour l'essai et l'application
 * (`requireKindWrite`) : comptes → `accounts.write`, articles → `catalog.write`, prix → `pricing.write`.
 */
export const KIND_PERMISSION: Record<ImportKind, string> = {
  accounts: 'accounts.write',
  products: 'catalog.write',
  prices: 'pricing.write',
  // Créances de l'ERP : `receivables.import` (contrat encaissement § 8).
  invoices: 'receivables.import',
  receipts: 'receivables.import',
  balances: 'receivables.import',
  // Objectifs (contrat objectifs § 5).
  targets: 'targets.write',
};

/** Genres que la personne peut importer. */
export function allowedKinds(can: (p: string) => boolean): ImportKind[] {
  return IMPORT_KINDS.filter((k) => can(KIND_PERMISSION[k]));
}

/** Libellé d'un champ cible selon le genre (« code » : code du compte, de l'article…). */
export function targetKey(kind: ImportKind, field: string): string {
  if (kind === 'accounts') return `imports.targets.${field}`;
  const group = { products: 'productTargets', prices: 'priceTargets', invoices: 'invoiceTargets', receipts: 'receiptTargets', balances: 'balanceTargets', targets: 'targetTargets' }[kind];
  return `imports.${group}.${field}`;
}

export const DRY_RUN_OUTCOMES: ImportOutcome[] = ['CREATE', 'UPDATE', 'UNCHANGED', 'ERROR'];
export const APPLY_OUTCOMES: ImportOutcome[] = ['CREATED', 'UPDATED', 'SKIPPED', 'ERROR'];
export const SYSTEM_PATTERN = /^[A-Z0-9_]{2,30}$/;

/** Fichier accepté ? (extension et taille vérifiées avant l'envoi ; le serveur revérifie). */
export function fileProblem(file: File): 'type' | 'size' | 'empty' | null {
  const name = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext))) return 'type';
  if (file.size === 0) return 'empty';
  if (file.size > MAX_IMPORT_BYTES) return 'size';
  return null;
}

/** Étape de l'assistant d'après l'état de l'import. */
export function stepOf(job: ImportJob): 'mapping' | 'dry-run' | 'result' {
  if (job.status === 'APPLIED' || job.status === 'APPLYING') return 'result';
  if (job.status === 'ANALYZED') return 'dry-run';
  return 'mapping';
}

/** Colonnes associées deux fois au même champ : en-tête → message. */
export function duplicateTargets(mapping: Record<string, string>): Record<string, string> {
  const seen = new Map<string, string>();
  const out: Record<string, string> = dict();
  for (const [header, field] of Object.entries(mapping)) {
    if (!field) continue;
    const first = seen.get(field);
    if (first !== undefined) out[header] = first;
    else seen.set(field, header);
  }
  return out;
}

/** Enregistre un fichier reçu de l'API (modèle, rapport) : lien temporaire, puis libéré. */
export function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const TEMPLATE_NAME: Record<ImportKind, string> = { accounts: 'comptes', products: 'articles', prices: 'prix', invoices: 'factures', receipts: 'reglements', balances: 'soldes', targets: 'objectifs' };

export async function downloadTemplate(api: Api, tenantId: string, format: 'xlsx' | 'csv', kind: ImportKind = 'accounts'): Promise<void> {
  const blob = await unwrap(api.GET('/imports/template', { params: { header: { 'X-Tenant-Id': tenantId }, query: { format, kind } }, parseAs: 'blob' }));
  saveBlob(blob as unknown as Blob, `modele-import-${TEMPLATE_NAME[kind]}.${format}`);
}

export async function downloadReport(api: Api, tenantId: string, job: ImportJob): Promise<void> {
  const blob = await unwrap(api.GET('/imports/{id}/report', { params: { path: { id: job.id }, header: { 'X-Tenant-Id': tenantId } }, parseAs: 'blob' }));
  saveBlob(blob as unknown as Blob, `rapport-import-${job.file_name.replace(/\.[^.]+$/, '')}.csv`);
}
